import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../App.css";
import Layout from "../Components/Layout";
import { fetchUserSubscriptions, fetchFibrePlans, regradeSubscription  } from "../apiService";
import { Subscription, PortalProduct } from "../Interface";
import { useGlobalError } from '../Components/GlobalErrorContext';

const ManagePackage = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal for confirmation
  const [packages, setPackages] = useState<PortalProduct[]>([]); // Use PortalProduct interface for packages
  const [selectedPackage, setSelectedPackage] = useState<PortalProduct | null>(null); // Use PortalProduct for selected package
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { showErrorModal } = useGlobalError();


  const portalEndCustomerId = localStorage.getItem("portalEndCustomerId");

  useEffect(() => {
    if (portalEndCustomerId) {
      fetchUserSubscriptions(portalEndCustomerId)
        .then((data) => {
          const foundSubscription = data.find(
            (sub: { reference: string | number | undefined; id?: number }) => 
              String(sub.reference) === subscriptionId || String(sub.id) === subscriptionId
          );
          if (foundSubscription) {
            setSubscription(foundSubscription);
          } else {
            setError("Subscription not found");
          }
          setLoading(false);
        })
        .catch((err) => {
          setError("Error fetching subscription details");
          setLoading(false);
        });

      // Fetch packages immediately
      fetchFibrePlans()
        .then((fetchedPackages) => {
          setPackages(fetchedPackages);
        })
        .catch((err) => {
          console.error("Error fetching packages:", err);
          setError("Error fetching packages");
        });
    }
  }, [portalEndCustomerId, subscriptionId]);

  const handlePackageSelect = (pkg: PortalProduct) => {
    setSelectedPackage(pkg); // Set the selected package
    console.log("Selected package:", pkg);
  };

  const handleSubmitPackageChange = () => {
    if (selectedPackage) {
      setShowModal(true); // Show confirmation modal
    } else {
      setError("Please select a package to proceed."); // Set error if no package is selected
    }
  };

const handleConfirmChange = async () => {
  setShowModal(false);
  setLoading(true);
  try {
    if (!subscription?.id || !selectedPackage?.id) {
      const msg = "Missing subscription or package ID.";
      setError(msg);
      showErrorModal(msg);
      setLoading(false);
      return;
    }

    const result = await regradeSubscription(subscription.id, selectedPackage.id);

    if (result.success) {
      console.log("Upgrade/Downgrade successful:", result.data);
      setError("");
      alert(result.message);
    } else {
      console.error("Upgrade/Downgrade failed:", result.message);
      setError(result.message);
      showErrorModal(result.message);
    }
  } catch (error: any) {
    console.error("Error during subscription upgrade/downgrade:", error);
    console.log("Full error object:", error);
    console.log("Error response?.data:", error?.response?.data);

    const detail = error.response?.data?.detail;

    if (Array.isArray(detail)) {
      const messages = detail.map((err) => err.msg).join(" | ");
      console.log("Validation error messages:", messages);
      showErrorModal(messages);
    } else if (typeof detail === "string") {
      console.log("Error detail string:", detail);
      showErrorModal(detail);
    } else if (error.message) {
      showErrorModal(error.message);
    } else {
      showErrorModal("An unexpected error occurred. Please try again.");
    }
  } finally {
    setLoading(false);
  }
};


  const handleCancelChange = () => {
    setShowModal(false); // Hide the modal
  };

  if (loading) return <p>Loading...</p>;
  if (!subscription) return <p>No subscription found.</p>;

  return (
    <Layout>
      <div className="container mt-5">
        <div className="card shadow-sm p-4">

            {/* Subscription Details Section */}
            <h4 style={{ color: "#E2520F", fontWeight: "bold"}}>PACKAGE DETAILS</h4>
            <hr />
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <th>Provider</th>
                  <td>FTTHSA</td>
                </tr>
                <tr>
                  <th>Subscription Reference</th>
                  <td>{subscription.accountReference}</td>
                </tr>
                <tr>
                  <th>Download Speed</th>
                  <td>{subscription.portalProduct?.speedDownMbps} Mbps</td>
                </tr>
                <tr>
                  <th>Upload Speed</th>
                  <td>{subscription.portalProduct?.speedUpMbps} Mbps</td>
                </tr>
                <tr>
                  <th>Price</th>
                  <td>{subscription.portalProduct?.price} {subscription.portalProduct?.currency} / Month</td>
                </tr>
                {/* <tr>
                  <th>Activation Fee</th>
                  <td>{subscription.portalProduct?.price} {subscription.portalProduct?.currency}</td>
                </tr> */}
                <tr>
                  <th>Billing Auto-Renew</th>
                  <td>{subscription.autoRenew ? "Enabled" : "Disabled"}</td>
                </tr>
                <tr>
                  <th>Subscription Status</th>
                  <td className={subscription.status === "active" ? "text-success" : "text-danger"}>
                    {subscription.status.replace("_", " ").toUpperCase()}
                  </td>
                </tr>
                <tr>
                  <th>Next Billing Date</th>
                  <td>{new Date(subscription.nextBillingDate).toLocaleDateString()}</td>
                </tr>
              </tbody>
            </table>
            <hr />

          <br />
        </div>
        <br />
        {subscription.status === "active" && (
        <div className="card shadow-sm p-4">
          {/* Change Package Section */}
          <h4 style={{ color: "#E2520F", fontWeight: "bold"}}>CHANGE PACKAGE</h4>
          <hr />
          <h5 style={{ margin: 0, color: "#ec4d13" }}>Select desired Package</h5>
          <br/>
          <div className="row">
            {packages.map((pkg) => (
              <div className="col-md-2 mb-3" key={pkg.id}>
                <div
                  className={`card ${selectedPackage?.id === pkg.id ? "border-primary" : ""}`}
                  onClick={() => handlePackageSelect(pkg)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-body">
                    <h5 className="card-title">{pkg.name}</h5>
                    <p className="card-text">Price: {pkg.price} p/m</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-between">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              style={{ width: '30%', backgroundColor: '#E2520F', color: 'white', borderColor:'#E2520F' }}
            >
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitPackageChange}
              disabled={!selectedPackage}
              style={{ width: '30%', backgroundColor: '#E2520F', borderColor: '#E2520F' }}
            >
              Submit Package Change
            </button>
          </div>

          <br />

          {/* Confirmation Modal */}
          {showModal && (
            <div className="modal show" tabIndex={-1} role="dialog" style={{ display: "block" }}>
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header" style={{ backgroundColor: '#e2520f', color: 'white' }}>
                    <h5 className="modal-title">Confirm Package Change</h5>
                  </div>

                  <div className="modal-body">
                    <p>
                      Are you sure you want to change the package? <br />
                      <strong>NB:</strong> Once you Upgrade/Downgrade, you will not be permitted to regrade during the cycle and will only be allowed to do so after the current active cycle.
                    </p>

                    <div className="mt-3">
                      <h6 className="fw-bold">Please note:</h6>
                      <ul className="text-muted small mb-3">
                        <li>New plan will be billed on your anniversary date.</li>
                        <li>Downgrades take effect only after the current cycle ends.</li>
                        <li>Upgrades are activated immediately.</li>
                        <li>Plan changes are locked in until next billing cycle.</li>
                      </ul>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="termsCheck"
                          checked={termsAccepted}
                          onChange={() => setTermsAccepted(!termsAccepted)}
                        />
                        <label className="form-check-label" htmlFor="termsCheck">
                          I have read and agree to the terms and conditions above.
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <div className="d-flex justify-content-between w-100">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancelChange}
                        style={{ backgroundColor: '#ec4d13', borderColor: '#ec4d13' }}
                      >
                        No, Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleConfirmChange}
                        disabled={!termsAccepted}
                        style={{ backgroundColor: '#ec4d13', borderColor: '#ec4d13' }}
                      >
                        Yes, Proceed
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
      )}
<br/>
<br/>
<br/>
<br/>
<br/>
<br/>
<br/>
<br/>

      </div>
    </Layout>
  );
};

export default ManagePackage;
